import { z } from 'zod';
/**
 * Typed shape of the dossier section payloads (dossier_forms.data_jsonb).
 *
 * Everything is optional: sections are autosaved partially over multiple days.
 * Which fields are REQUIRED (incl. conditionals) is defined in dossier-rules.ts
 * and enforced at submit time — not here.
 *
 * NOTE: docs/INTAKE-DOSSIER-MAPPING.md is not in the repo yet; this module is
 * the reconstruction from the build brief and is the single source of truth
 * for portal + backend until that mapping lands. Keep changes additive.
 */
const bounded = (max = 200) => z.string().trim().max(max);
const money = z.number().min(0).max(100_000_000);
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD')
    .refine((s) => !Number.isNaN(Date.parse(s)), 'invalid date');
export const salutationSchema = z.enum(['mr', 'ms']);
export const civilStatusSchema = z.enum([
    'single',
    'married',
    'registered_partnership',
    'divorced',
    'widowed',
]);
/** One borrower's personal data (Kreditnehmer 1 / 2). */
export const borrowerPersonSchema = z
    .object({
    salutation: salutationSchema,
    firstName: bounded(100),
    lastName: bounded(100),
    birthDate: isoDate,
    nationality: bounded(2).toUpperCase(), // ISO 3166-1 alpha-2
    /** Required (by rules) when nationality !== 'CH'. */
    residencePermit: z.enum(['B', 'C', 'G', 'L', 'other']),
    civilStatus: civilStatusSchema,
    email: z.string().trim().toLowerCase().email().max(254),
    phone: bounded(32),
    street: bounded(200),
    postalCode: bounded(10),
    city: bounded(100),
})
    .partial();
export const applicantSectionSchema = z
    .object({
    borrower1: borrowerPersonSchema,
    hasSecondBorrower: z.boolean(),
    borrower2: borrowerPersonSchema,
})
    .partial();
export const householdSectionSchema = z
    .object({
    adults: z.number().int().min(1).max(10),
    children: z.number().int().min(0).max(15),
    childrenBirthYears: bounded(100),
    /** Whether anyone in the household pays alimony/support (details in liabilities). */
    plannedChanges: bounded(1000),
})
    .partial();
export const employmentStatusSchema = z.enum([
    'employed',
    'self_employed',
    'retired',
    'not_employed',
]);
export const borrowerEmploymentSchema = z
    .object({
    status: employmentStatusSchema,
    employer: bounded(200), // company name when self-employed
    position: bounded(200),
    employedSince: isoDate,
    workloadPct: z.number().int().min(1).max(100),
    isTemporary: z.boolean(),
})
    .partial();
export const employmentSectionSchema = z
    .object({
    borrower1: borrowerEmploymentSchema,
    borrower2: borrowerEmploymentSchema,
})
    .partial();
export const borrowerIncomeSchema = z
    .object({
    annualGrossIncome: money,
    hasThirteenthSalary: z.boolean(),
    bonusAvg3y: money,
    otherIncome: money,
    otherIncomeDescription: bounded(500),
})
    .partial();
export const incomeSectionSchema = z
    .object({
    borrower1: borrowerIncomeSchema,
    borrower2: borrowerIncomeSchema,
    rentalIncomeAnnual: money,
})
    .partial();
export const assetsSectionSchema = z
    .object({
    /** Total equity the customer plans to bring in (prefilled from calculator). */
    totalEquityPlanned: money,
    cashSavings: money,
    securities: money,
    usePillar2: z.boolean(),
    pillar2Amount: money,
    usePillar3a: z.boolean(),
    pillar3aAmount: money,
    giftOrAdvance: money,
    otherAssets: bounded(500),
})
    .partial();
export const liabilitiesSectionSchema = z
    .object({
    hasLoans: z.boolean(),
    loansMonthlyPayment: money,
    loansOutstanding: money,
    hasLeasing: z.boolean(),
    leasingMonthlyPayment: money,
    hasAlimony: z.boolean(),
    alimonyMonthlyPayment: money,
    hasOtherLiabilities: z.boolean(),
    otherLiabilitiesDescription: bounded(500),
})
    .partial();
export const financingPurposeSchema = z.enum(['purchase', 'refinance', 'new_build']);
export const propertyUsageSchema = z.enum(['owner_occupied', 'rented', 'vacation']);
export const propertyTypeSchema = z.enum([
    'single_family_house',
    'condominium', // Eigentumswohnung / Stockwerkeigentum (STWE)
    'multi_family_house',
    'building_land',
]);
export const propertySectionSchema = z
    .object({
    financingPurpose: financingPurposeSchema, // Kauf / Ablösung / Neubau
    usage: propertyUsageSchema,
    propertyType: propertyTypeSchema,
    street: bounded(200),
    postalCode: bounded(10),
    city: bounded(100),
    canton: bounded(2).toUpperCase(),
    /** Required when purchasing (prefilled from calculator propertyValue). */
    purchasePrice: money,
    /** Required when refinancing: today's estimated market value. */
    currentMarketValue: money,
    constructionYear: z.number().int().min(1500).max(2100),
    livingAreaSqm: z.number().min(1).max(10_000),
    renovationsPlanned: bounded(1000),
    /** Baurecht. */
    isLeasehold: z.boolean(),
    leaseholdAnnualRent: money,
    /** Stockwerkeigentum — only relevant for propertyType=condominium. */
    stwe: z
        .object({
        valueQuotaPermille: z.number().min(0).max(1000),
        renewalFundBalance: money,
        monthlyCondoFees: money,
        managementCompany: bounded(200),
    })
        .partial(),
    /** Finanzierung details. */
    financing: z
        .object({
        desiredMortgageAmount: money,
        refinanceCurrentLender: bounded(200),
        refinanceCurrentBalance: money,
        refinanceFixedTermEnd: isoDate,
        newBuildTotalProjectCost: money,
        newBuildLandCost: money,
        newBuildConstructionCost: money,
    })
        .partial(),
})
    .partial();
export const documentsSectionSchema = z
    .object({
    notes: bounded(1000),
})
    .partial();
export const reviewSectionSchema = z
    .object({
    remarks: bounded(2000),
})
    .partial();
/** section → payload schema; used by the portal forms and the submit validation. */
export const dossierDataSchemas = {
    applicant: applicantSectionSchema,
    household: householdSectionSchema,
    income: incomeSectionSchema,
    assets: assetsSectionSchema,
    liabilities: liabilitiesSectionSchema,
    property: propertySectionSchema,
    employment: employmentSectionSchema,
    documents: documentsSectionSchema,
    review: reviewSectionSchema,
};
/** The whole dossier as { section: data } — the unit the rules operate on. */
export const dossierDataSchema = z
    .object({
    applicant: applicantSectionSchema,
    household: householdSectionSchema,
    income: incomeSectionSchema,
    assets: assetsSectionSchema,
    liabilities: liabilitiesSectionSchema,
    property: propertySectionSchema,
    employment: employmentSectionSchema,
    documents: documentsSectionSchema,
    review: reviewSectionSchema,
})
    .partial();
